import React from 'react';
import { Save, CheckCircle2, FileDown, Presentation, FileText, Loader2 } from 'lucide-react';

export default function PublishBar({ confirmCount = 0, isConfirmed = false, onExport, isLocked = false, isSaving = false }) {
  const isSaved = Boolean(isConfirmed);

  return (
    <footer 
      className="publish-bar-container publish-section w-full border-t border-slate-800 bg-[#020617] shrink-0 px-3 flex items-center z-50 sticky bottom-0 left-0 right-0 shadow-2xl"
      style={{ position: 'sticky', bottom: 0, height: '63px', minHeight: '63px', maxHeight: '63px', backgroundColor: '#020617', zIndex: 50, flexShrink: 0 }}
    >
      {/* Arranged side-by-side in a single row */}
      <div className="grid grid-cols-4 gap-1.5 w-full items-center">
        {/* 1. Manual Save / AutoSave Trigger Button */}
        <button
          type="button"
          onClick={() => !isLocked && onExport('confirm')}
          disabled={isLocked || isSaving}
          className={`h-[38px] px-2.5 rounded-xl flex items-center justify-center gap-1.5 text-xs font-bold transition-all truncate ${
            isLocked
              ? 'bg-[#1F1F1F] text-gray-500 cursor-not-allowed border border-transparent shadow-none'
              : isSaving
                ? 'bg-[#0C5E31] text-emerald-200 border border-emerald-500/30 shadow-none'
                : isSaved
                  ? 'bg-[#107C41] hover:bg-[#0C5E31] text-white shadow-md shadow-[#107C41]/25 active:scale-95 cursor-pointer font-bold border border-emerald-500/40'
                  : 'bg-[#107C41] hover:bg-[#0C5E31] text-white shadow-md shadow-[#107C41]/25 active:scale-95 cursor-pointer font-bold'
          }`}
          title={isLocked ? "Select or create a project first" : "Click to manually save all photo list updates & project changes"}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <span className="truncate">Saving...</span>
            </>
          ) : isSaved ? (
            <>
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" />
              <span className="truncate">{confirmCount > 0 ? `Saved (${confirmCount})` : 'Saved'}</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4 shrink-0" />
              <span className="truncate">{confirmCount > 0 ? `Save (${confirmCount})` : 'Save'}</span>
            </>
          )}
        </button>

        {/* 2. PDF Button */}
        <button
          type="button"
          onClick={() => !isLocked && isSaved && onExport('pdf')}
          disabled={isLocked || !isSaved}
          className={`h-[38px] px-2.5 rounded-xl flex items-center justify-center gap-1.5 text-xs transition-all truncate ${
            isLocked
              ? 'bg-[#1F1F1F] text-gray-500 cursor-not-allowed border border-transparent shadow-none'
              : isSaved
                ? 'bg-white hover:bg-slate-100 text-[#F40F02] font-bold border border-transparent shadow-md shadow-white/20 active:scale-95 cursor-pointer'
                : 'bg-slate-900 border border-slate-800 text-slate-500 font-bold opacity-60 cursor-not-allowed shadow-none'
          }`}
          title={isLocked ? "Select or create a project first" : isSaved ? "Export report to PDF (.pdf)" : "Save changes before exporting"}
        >
          <FileDown className={`h-4 w-4 shrink-0 ${!isLocked && isSaved ? 'text-[#F40F02]' : 'text-slate-500'}`} />
          <span className="truncate">PDF</span>
        </button>

        {/* 3. PPT Button */}
        <button
          type="button"
          onClick={() => !isLocked && isSaved && onExport('ppt')}
          disabled={isLocked || !isSaved}
          className={`h-[38px] px-2.5 rounded-xl flex items-center justify-center gap-1.5 text-xs font-bold transition-all truncate ${
            isLocked
              ? 'bg-[#1F1F1F] text-gray-500 cursor-not-allowed border border-transparent shadow-none'
              : isSaved
                ? 'bg-[#D04423] hover:bg-[#B8381C] text-white shadow-md shadow-[#D04423]/25 border border-transparent active:scale-95 cursor-pointer'
                : 'bg-slate-900 border border-slate-800 text-slate-500 opacity-60 cursor-not-allowed shadow-none'
          }`}
          title={isLocked ? "Select or create a project first" : isSaved ? "Export report to Microsoft PowerPoint (.pptx)" : "Save changes before exporting"}
        >
          <Presentation className="h-4 w-4 shrink-0" />
          <span className="truncate">PPT</span>
        </button>

        {/* 4. DOC Button */}
        <button
          type="button"
          onClick={() => !isLocked && isSaved && onExport('word')}
          disabled={isLocked || !isSaved}
          className={`h-[38px] px-2.5 rounded-xl flex items-center justify-center gap-1.5 text-xs font-bold transition-all truncate ${
            isLocked
              ? 'bg-[#1F1F1F] text-gray-500 cursor-not-allowed border border-transparent shadow-none'
              : isSaved
                ? 'bg-[#185ABD] hover:bg-[#144796] text-white shadow-md shadow-[#185ABD]/25 border border-transparent active:scale-95 cursor-pointer'
                : 'bg-slate-900 border border-slate-800 text-slate-500 opacity-60 cursor-not-allowed shadow-none'
          }`}
          title={isLocked ? "Select or create a project first" : isSaved ? "Export report to Microsoft Word (.docx)" : "Save changes before exporting"}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">DOC</span>
        </button>
      </div>
    </footer>
  );
}
